import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong, faEnvelope, faPhone, faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faTwitter, faFacebook, faInstagram, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { API_BASE_URL } from "../../api";
import useSectionNav from "../../hooks/useSectionNav";
import "./Footer.scss";

const linkGroups = [
  {
    titleKey: "landing.footer.col_product",
    items: [
      { labelKey: "landing.nav.features",       href: "#features" },
      { labelKey: "landing.nav.pricing",        href: "#pricing" },
      { labelKey: "landing.nav.testimonials",   href: "#trust" },
    ],
  },
  {
    titleKey: "landing.footer.col_support",
    items: [
      { labelKey: "landing.nav.help_center",    href: "/help" },
      { labelKey: "landing.nav.contact",        href: "/contact" },
    ],
  },
  {
    titleKey: "landing.footer.col_legal",
    items: [
      { labelKey: "landing.footer.legal_notice", href: "/legal-notice" },
      { labelKey: "landing.footer.privacy",      href: "/privacy" },
      { labelKey: "landing.footer.terms",        href: "/terms" },
    ],
  },
];

const socials = [
  { icon: faLinkedin, href: "https://www.linkedin.com/company/saydalani", label: "LinkedIn" },
  { icon: faTwitter,   href: "https://www.x.com/saydalanicom", label: "Twitter" },
  { icon: faFacebook, href: "https://www.facebook.com/saydalani.officiel/", label: "Facebook" },
  { icon: faInstagram, href: "https://www.instagram.com/saydalani.officiel/", label: "Instagram" },
  { icon: faYoutube, href: "https://www.youtube.com/@saydalani/", label: "YouTube" },
];

// fluid: full width with the navbar's side gutters, for pages whose content is full width
// (product page) — otherwise the centered Bootstrap container, like the landing sections.
const Footer = ({ fluid = false }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const onSectionClick = useSectionNav();
  const [email, setEmail]       = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]   = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    axios.post(`${API_BASE_URL}/api/v1/newsletter/subscribe`, { email })
      .then(() => {
        setSubmitted(true);
        setEmail("");
      })
      .catch(() => toast.error(t("landing.footer.newsletter_error")))
      .finally(() => setSending(false));
  };

  return (
    <footer className="syd-footer">
      <div className={fluid ? "container-fluid px-4 px-lg-5" : "container"}>

        {/* ── Top grid ── */}
        <div className="syd-footer__grid">

          {/* Brand column */}
          <div className="syd-footer__brand-col">
            <NavLink to={`/${i18n.language}`} className="syd-footer__brand">
              <img src="/images/saydalani_logo.png" alt="Saydalani" className="syd-footer__logo" />
            </NavLink>
            <p className="syd-footer__tagline">
              {t("landing.footer.tagline")}
            </p>

            <div className="syd-footer__contact">
              <div className="syd-footer__contact-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>contact@saydalani.com</span>
              </div>
              <div className="syd-footer__contact-item">
                <FontAwesomeIcon icon={faPhone} />
                <span>+212 6 09 89 92 72</span>
              </div>
              <div className="syd-footer__contact-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>Berkane, Maroc</span>
              </div>
            </div>

            <div className="syd-footer__socials">
              {socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="syd-footer__social" aria-label={s.label}>
                  <FontAwesomeIcon icon={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {linkGroups.map(({ titleKey, items }) => (
            <div className="syd-footer__link-col" key={titleKey}>
              <h4 className="syd-footer__col-title">{t(titleKey)}</h4>
              <ul className="syd-footer__list">
                {items.map((item) => {
                  const isSectionLink = item.href.startsWith("#");
                  const isPrefixedRoute = !isSectionLink && item.href !== "/";
                  const to = isSectionLink
                    ? (location.pathname !== `/${i18n.language}` ? `/${i18n.language}${item.href}` : item.href)
                    : (isPrefixedRoute ? `/${i18n.language}${item.href}` : item.href);
                  return (
                    <li key={item.labelKey}>
                      <NavLink
                        to={to}
                        onClick={isSectionLink ? onSectionClick(item.href.slice(1)) : undefined}
                        className="syd-footer__link"
                      >
                        {t(item.labelKey)}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div className="syd-footer__newsletter-col">
            <h4 className="syd-footer__col-title">{t("landing.footer.newsletter")}</h4>
            <p className="syd-footer__newsletter-desc">
              {t("landing.footer.newsletter_desc")}
            </p>
            {submitted ? (
              <div className="syd-footer__success">
                {t("landing.footer.newsletter_success")}
              </div>
            ) : (
              <form className="syd-footer__newsletter-form" onSubmit={handleSubmit}>
                <input
                  type="email"
                  placeholder={t("landing.footer.newsletter_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" disabled={sending}>
                  <FontAwesomeIcon icon={faArrowRightLong} />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="syd-footer__divider" />

        {/* ── Bottom bar ── */}
        <div className="syd-footer__bottom">
          <span>{t("landing.footer.copyright", { year: new Date().getFullYear() })}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
